// Mock data for Vignesh Sudhakar's Portfolio
// All interactive data lives here. Backend integration will replace contact form submission.

export const profile = {
  name: "Vignesh Sudhakar",
  title: "Technical Lead — Business Analyst / QA",
  tagline: "Bridging business strategy & quality engineering across Banking and Capital Markets for 12+ years.",
  location: "Toronto, Ontario, Canada",
  email: "vigneshs.official@gmail.com",
  phone: "+1-438-778-6062",
  linkedin: "https://www.linkedin.com/in/vigneshsofficial/",
  yearsExperience: 12,
  // Replace with personal headshot. Using a curated professional placeholder.
  photo: "https://customer-assets.emergentagent.com/job_resume-pro-263/artifacts/520m0qkg_ProfessionalHeadshot-AI.png",
  heroBackdrop: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=70",
};

export const aboutParagraphs = [
  "I am a Technical Lead with over a decade of experience driving quality engineering and business analysis within Banking and Financial Services. My career has been defined by bridging the gap between business stakeholders and engineering teams — translating complex regulatory, capital markets, and portfolio management requirements into resilient, scalable software.",
  "From shaping test strategy and orchestrating UAT for enterprise platforms, to architecting automation frameworks and CI/CD pipelines, I bring a holistic perspective: every release I touch is measured by business outcome, not just test coverage.",
  "I have led cross-functional teams across Canada and India, partnered with custodians, front-office desks, and risk officers, and championed accessibility (WCAG) and analytics validation as core quality dimensions.",
];

export const stats = [
  { value: "12+", label: "Years in Banking & FinServ" },
  { value: "40+", label: "Releases Delivered" },
  { value: "5", label: "Automation Frameworks Built" },
  { value: "2", label: "Industry Certifications" },
];

export const skillGroups = [
  {
    category: "Quality Assurance & Testing",
    items: [
      "Test Strategy, Planning & Execution",
      "Functional, Integration & Regression",
      "ETL / Data Validation (SQL)",
      "API Testing (Postman)",
      "Accessibility (WCAG, Axe, NVDA, Wave)",
    ],
  },
  {
    category: "Business Analysis & Leadership",
    items: [
      "Requirements Gathering & Specs",
      "User Story Creation & Backlog Grooming",
      "Stakeholder Management",
      "UAT Planning, Coordination & Sign-off",
      "Process Improvement & Gap Analysis",
    ],
  },
  {
    category: "Automation & Engineering",
    items: [
      "Selenium (Cucumber, TestNG, Maven)",
      "Playwright (JavaScript)",
      "UFT, TestComplete, QF-Test, TOSCA",
      "Java, SQL, VBScript, JSON, XML",
      "Bitbucket, Bamboo, Ansible CI/CD",
    ],
  },
  {
    category: "Tools & Collaboration",
    items: [
      "JIRA, HP ALM, Zephyr",
      "Confluence",
      "Agile / Scrum / SAFe",
      "Power BI / Reporting Dashboards",
      "Defect Triage & RCA",
    ],
  },
];

export const experiences = [
  {
    company: "FNZ",
    slug: "fnz",
    tabLabel: "FNZ",
    location: "Toronto, Ontario, Canada",
    role: "Solution Consultant / QA Lead",
    period: "Apr 2023 — Feb 2026",
    featured: true,
    summary:
      "Technical Lead spanning automation, functional QA and business analysis on the BMO — FNZ wealth management platform migration.",
    roles: [
      {
        title: "Automation Test Lead",
        points: [
          "Led the automation testing team in designing and maintaining a hybrid automation framework using Selenium, Java and Maven for large-scale regression testing.",
          "Developed reusable automation components and optimized regression execution for faster release cycles.",
          "Integrated automation suites with CI/CD pipelines and supported continuous testing practices.",
          "Coordinated automation planning, defect triaging, execution tracking and release validation across multiple Agile teams.",
          "Mentored team members on framework standards, coding practices and automation strategy.",
        ],
      },
      {
        title: "Functional QA Lead",
        points: [
          "Led the functional testing team within an Agile delivery model using JIRA and Zephyr for sprint and test management.",
          "Created and executed functional, integration, regression and UAT test scenarios across onboarding, portfolio management, trading and reporting modules.",
          "Participated in sprint planning, backlog grooming, requirement walkthroughs and release ceremonies.",
          "Collaborated with Product Owners, developers and stakeholders to validate business requirements and resolve defects.",
          "Ensured end-to-end quality assurance and production readiness for critical financial platform releases.",
        ],
      },
      {
        title: "Solution Consultant / Business Analyst",
        points: [
          "Worked as a Solution Consultant and Business Analyst to identify business gaps and recommend functional solutions for portfolio management platforms.",
          "Conducted stakeholder discussions and requirement gathering workshops to capture business and system needs.",
          "Performed gap analysis between legacy BMO systems and FNZ platform capabilities during migration initiatives.",
          "Created process flows, workflow diagrams, UI mockups and solution documentation using Visio, Figma, Lucidchart and Confluence.",
          "Translated business requirements into functional specifications and collaborated with development and QA teams for successful delivery.",
          "Led UAT planning and coordination activities with client stakeholders — environment readiness, infrastructure setup, user onboarding, test execution support and defect tracking throughout the UAT cycle.",
        ],
      },
    ],
    project: {
      title: "Project Overview",
      overview:
        "FNZ is a wealth management platform provider offering end-to-end banking and investment solutions for financial institutions. BMO partnered with FNZ to modernize and migrate its existing wealth management platform onto FNZ products. The platform consisted of multiple integrated modules supporting client onboarding, portfolio management, trading, payments, reporting, reconciliation, benchmark management and forex trading.",
      modules: [
        {
          name: "Portfolio Review Page",
          tagline:
            "A portfolio management platform used by financial advisors to manage client investment accounts and portfolios.",
          features: [
            "Create and submit BUY/SELL trades for investment instruments",
            "View portfolio holdings and performance",
            "Compare portfolio performance against predefined benchmarks",
            "Add and manage sub-accounts",
            "Validate portfolio allocation against client risk profiles and investment objectives",
            "Access investment reports and portfolio insights",
          ],
        },
        {
          name: "BMO Onboarding",
          tagline:
            "An onboarding platform used by advisors to create and configure investment accounts for clients.",
          features: [
            "Capture client KYC and personal information",
            "Maintain financial assets and liabilities",
            "Configure investment objectives and risk tolerance",
            "Manage account and reporting preferences",
            "Capture tax and compliance-related information",
          ],
        },
        {
          name: "X-Hub",
          tagline:
            "A centralized portfolio and investment management platform designed for large-scale and institutional investment operations.",
          features: [
            "Manage large investment accounts and portfolios",
            "Create and maintain model portfolios",
            "Bundle investment instruments based on investment strategies and eligibility criteria",
            "Subscribe multiple client accounts to model portfolios",
            "Execute bulk trades across subscribed accounts",
            "Perform tax-loss harvesting activities",
            "Support asset rebalancing and portfolio maintenance operations",
          ],
        },
      ],
    },
  },
  {
    company: "Virtusa Consulting",
    slug: "virtusa-canada",
    tabLabel: "Virtusa Canada",
    location: "Toronto, Ontario, Canada",
    role: "Agile Business Analyst / Functional QA Lead",
    period: "Jun 2022 — Apr 2023",
    featured: true,
    summary:
      "Dual-role engagement spanning Agile Business Analysis on BMO Gateway Management for HNI clients and Functional QA leadership on BMO InvestorLine for self-directed retail investors.",
    roles: [
      {
        title: "Agile Business Analyst",
        points: [
          "Gathered and analyzed business and functional requirements for the BMO Gateway Management platform designed for High Net Worth Individuals (HNI).",
          "Conducted stakeholder discussions and requirement workshops with business users, advisors and product teams to understand wealth management workflows.",
          "Created user stories, acceptance criteria, process flows and functional specifications for portfolio management and advisor-client interaction modules.",
          "Worked on functionalities related to portfolio viewing, benchmark comparison, asset allocation visibility, advisor communication, document requests and trade confirmation tracking.",
          "Collaborated with technical teams to define integrations with external systems such as Broadridge for trade confirmations and transaction history retrieval.",
          "Created workflow diagrams and UI mockups using Visio, Figma, Lucidchart and Confluence.",
          "Performed gap analysis between existing banking workflows and target-state digital wealth management solutions.",
          "Coordinated UAT activities with client stakeholders — environment setup, infrastructure readiness, user onboarding and business validation support.",
          "Worked closely with Product Owners, developers, QA teams and infrastructure teams to ensure smooth Agile delivery and release execution.",
        ],
      },
      {
        title: "Agile Functional QA Lead",
        points: [
          "Led the functional QA team for the BMO InvestorLine platform designed for retail investors to independently manage investments and trading activities.",
          "Prepared test strategies, test plans, test scenarios and test cases for trading, portfolio management, market insights and investment workflows.",
          "Executed functional, integration, regression and end-to-end testing activities within Agile sprint cycles.",
          "Validated functionalities related to trade creation, portfolio management, market insights, benchmark tracking and financial product management.",
          "Performed testing for real-time market data integrations with Morningstar and S&P services.",
          "Managed defect lifecycle activities including defect logging, prioritization, retesting and closure validation using JIRA and Zephyr.",
          "Coordinated release validation, sprint testing and production readiness activities across multiple releases.",
          "Worked closely with developers, Product Owners and business stakeholders to ensure quality delivery and timely defect resolution.",
          "Prepared QA status reports, defect metrics and release summaries for project stakeholders and leadership teams.",
        ],
      },
    ],
    project: {
      title: "Platforms Delivered",
      overview:
        "Engaged on two BMO wealth management platforms covering both advisor-managed (HNI) and self-directed (retail) investor segments — from portfolio visibility and benchmark tracking to live trading and market data integration.",
      modules: [
        {
          name: "BMO Gateway Management Platform",
          tagline:
            "A wealth management platform designed for High Net Worth Individuals (HNI), where investment accounts and portfolios are managed by dedicated financial advisors. Provides clients with visibility into their wealth management activities and portfolio performance.",
          features: [
            "View portfolio holdings and overall account balance",
            "Track asset growth against selected benchmarks and market indices",
            "Access trade confirmations and transaction history retrieved from Broadridge",
            "View detailed asset and investment allocations",
            "Secure messaging and communication with financial advisors",
            "Raise and manage document requests",
            "Access portfolio reports and investment insights",
          ],
        },
        {
          name: "BMO InvestorLine Platform",
          tagline:
            "A self-directed wealth management and trading platform designed for retail investors to independently manage their investments and trading activities.",
          features: [
            "Create and submit investment trade requests",
            "View and manage investment portfolios",
            "Access market insights and investment research",
            "Monitor benchmarks and market performance",
            "View real-time instrument prices and market standings integrated from Morningstar and S&P",
            "Explore financial products and investment opportunities",
            "Access trading functionalities and investment dashboards",
            "Track portfolio growth and investment performance",
          ],
        },
      ],
    },
  },
  {
    company: "Virtusa",
    slug: "virtusa-india",
    tabLabel: "Virtusa India",
    location: "Chennai, Tamil Nadu, India",
    role: "Test Automation Lead",
    period: "May 2019 — Jun 2022",
    featured: true,
    summary:
      "Led automation delivery across three BMO enterprise initiatives — from .NET advisor management and Java Swing commercial lending to a full CI/CD modernization program.",
    roles: [
      {
        title: "Responsibilities",
        points: [
          "Collaborated with business teams to analyze requirements and design test solutions.",
          "Built automation frameworks and CI/CD pipelines (Bitbucket, Bamboo, Ansible).",
          "Led data migration validation and ETL testing initiatives.",
          "Conducted code reviews and ensured quality governance.",
          "Delivered training sessions and documentation for business users.",
          "Validated the AUT is accessibility compatible as per WCAG guidelines.",
        ],
      },
    ],
    project: {
      title: "Projects Delivered",
      overview:
        "Three distinguished BMO engagements spanning insurance advisor lifecycle automation, commercial lending QA on legacy Java Swing, and enterprise-wide CI/CD modernization — each with a tailored hybrid automation framework built on the Page Object Model.",
      modules: [
        {
          name: "BMO Advisor Management Application",
          tagline:
            "A .NET platform developed for BMO Insurance to manage advisors participating in insurance sales and servicing operations — supporting onboarding, profile management, account maintenance, commission calculations, policy tracking, advisor activation/deactivation and advisor code generation. Automation built on a hybrid keyword + data-driven framework using the Page Object Model.",
          features: [
            "Tech: .NET · UFT · VBScript",
            "Hybrid automation framework (keyword + data-driven)",
            "Page Object Model (POM) design pattern",
            "Advisor onboarding and profile management flows",
            "Commission calculation and policy tracking validation",
            "Advisor activation/deactivation and code generation",
          ],
        },
        {
          name: "BMO Loan IQ",
          tagline:
            "A Java Swing based commercial lending platform used by BMO's loan division to create, manage and approve commercial loans — covering loan origination workflows, approval processes, customer account management and commercial banking operations. UFT was integrated with HP ALM for centralized test management and automated execution through ALM test sets.",
          features: [
            "Tech: Java Swing · UFT · VBScript · HP ALM",
            "Hybrid keyword + data-driven automation framework",
            "Page Object Model (POM) architecture",
            "HP ALM — UFT integration for centralized execution",
            "Loan origination and approval workflow coverage",
            "Customer account management validation",
          ],
        },
        {
          name: "BMO Transformation Project",
          tagline:
            "A technical modernization initiative to implement an end-to-end CI/CD pipeline for enterprise application deployment and automated testing. Replaced manual Windows-server deployments with an integrated solution — migrating source control from TFS to Bitbucket, configuring Bamboo pipelines with SonarQube analysis, JFrog Artifactory for artifacts and Ansible Tower for deployment automation, with UFT suites executed for post-deployment validation.",
          features: [
            "Tech: Bitbucket · Bamboo · SonarQube · JFrog Artifactory",
            "Ansible Tower · UFT · VBScript · Windows Server",
            "TFS → Bitbucket source control migration",
            "Automated builds + SonarQube code quality gates",
            "Artifact management via JFrog Artifactory",
            "Ansible Tower driven Windows deployment automation",
            "UFT post-deployment validation integrated in the pipeline",
          ],
        },
      ],
    },
  },
  {
    company: "Infrasoft Technologies",
    slug: "infrasoft",
    tabLabel: "Infrasoft Technologies",
    location: "Chennai, Tamil Nadu, India",
    role: "Senior Automation Engineer",
    period: "Oct 2016 — May 2019",
    featured: true,
    summary:
      "Designed hybrid automation frameworks and led ROI / feasibility studies for QA modernization — delivered on BNP Paribas' global custodian banking platform across Singapore, Mumbai, Australia and France.",
    roles: [
      {
        title: "Responsibilities",
        points: [
          "Designed hybrid automation frameworks adopted across multiple business lines.",
          "Performed ROI analysis and feasibility studies for tooling and framework decisions.",
          "Executed regression and functional cycles across multi-release programs.",
          "Supported multi-region rollout validation spanning Singapore, Mumbai, Australia and France.",
          "Validated SWIFT messaging, settlement and reconciliation flows end-to-end.",
        ],
      },
    ],
    project: {
      title: "Project Delivered",
      overview:
        "BNP Paribas Custodian Banking Application — an enterprise custodian banking platform designed to manage end-to-end custody and securities operations for global financial markets. Supports daily operational activities for custodians including account management, securities processing, settlement operations, payment processing and corporate action management.",
      modules: [
        {
          name: "BNP Paribas — Custodian Banking Application",
          tagline:
            "End-to-end custody and securities operations across global markets. Users create and manage safekeeping accounts, branches, stock exchanges, central depositories, cash accounts, financial instruments, trading accounts, trades and corporate actions. SWIFT messaging enables secure communication with market exchanges and financial institutions for settlement processing.",
          features: [
            "Safekeeping accounts, branches, stock exchanges and central depositories",
            "Cash accounts, financial instruments and trading account management",
            "Trade lifecycle management and corporate action processing",
            "SWIFT messaging for market communication and settlement",
            "Multi-rail payment processing — EFT, ACT and NEFT",
            "Intelligent reconciliation — trade merging, settlement validation",
            "Cross-region reconciliation across multiple custodial accounts",
            "Deployed across Singapore, Mumbai, Australia and France",
            "Regional customization, regulatory changes and settlement workflow enhancements",
          ],
        },
        {
          name: "Domain & Technology Areas",
          tagline:
            "A concentrated set of custodian banking and capital markets domains touched across the engagement — spanning securities processing, settlement, corporate actions and multi-region banking operations.",
          features: [
            "Custodian Banking",
            "Securities Processing",
            "SWIFT Messaging",
            "Trade Settlement",
            "Corporate Actions",
            "Reconciliation",
            "Financial Instruments",
            "Payment Processing (EFT · ACT · NEFT)",
            "Multi-region Banking Operations",
          ],
        },
      ],
    },
  },
  {
    company: "Accenture",
    slug: "accenture",
    tabLabel: "Accenture Services Pvt Limited",
    location: "Chennai, Tamil Nadu, India",
    role: "Software Test Engineer",
    period: "May 2013 — Oct 2016",
    featured: true,
    summary:
      "Foundational years performing functional, integration and backend validation on Bank of America's Enterprise Issue Management System (eIMS) — a MetricStream-powered governance, risk and compliance platform.",
    roles: [
      {
        title: "Responsibilities",
        points: [
          "Performed functional and integration testing for enterprise GRC programs.",
          "Wrote SQL queries for backend validation and data integrity checks.",
          "Created RTM and managed the full defect lifecycle from logging to closure.",
          "Validated SSO, IAM and role-based access control across user personas.",
          "Supported workflow configuration validation across issue lifecycle stages.",
        ],
      },
    ],
    project: {
      title: "Project Delivered",
      overview:
        "eIMS — Enterprise Issue Management System used by the bank to record, manage, track and resolve issues related to compliance, legal, regulatory, audit and operational risk management across the organization. Provides an end-to-end workflow-driven solution for issue lifecycle management — from issue identification and assessment through remediation, approval, tracking and closure.",
      modules: [
        {
          name: "eIMS — Enterprise Issue Management System",
          tagline:
            "Supports multiple user roles and operational workflows involving risk teams, compliance teams, auditors, operational users and management stakeholders. Includes configurable workflows, issue categorization, escalation handling, approval processes, remediation tracking and reporting functionalities for enterprise governance and compliance. Built on the MetricStream product suite with enterprise SSO and IAM integrations.",
          features: [
            "Issue identification, assessment, remediation, approval and closure",
            "Multi-role operational workflows (risk, compliance, audit, operations)",
            "Configurable workflows and issue categorization",
            "Escalation handling and approval processes",
            "Remediation tracking and reporting functionalities",
            "Enterprise governance and compliance support",
            "MetricStream product suite integration",
            "Single Sign-On (SSO) and Identity & Access Management (IAM)",
            "Role-based access control and user provisioning",
          ],
        },
        {
          name: "Domain & Technology Areas",
          tagline:
            "A governance, risk and compliance focused technology stack combining a specialist GRC product suite with enterprise identity and workflow orchestration capabilities.",
          features: [
            "MetricStream",
            "Enterprise Risk Management",
            "Compliance Management",
            "Regulatory Reporting",
            "Workflow Management",
            "Single Sign-On (SSO)",
            "Identity & Access Management (IAM)",
            "Role-Based Access Control (RBAC)",
            "User Provisioning",
            "Operational Risk Management",
          ],
        },
      ],
    },
  },
];

export const domainExpertise = [
  {
    title: "Capital Markets",
    description: "Trade lifecycle, SWIFT messaging, reconciliation and post-trade processing for institutional desks.",
  },
  {
    title: "Portfolio Management",
    description: "Assets, benchmarks, holdings, performance attribution and investment workflow validation.",
  },
  {
    title: "Custodian & Front Office",
    description: "Custody platforms, order management and front-to-back integration with downstream risk systems.",
  },
  {
    title: "Insurance & Underwriting",
    description: "Policy administration, underwriting workflows and premium calculation validation.",
  },
  {
    title: "Regulatory & Risk Reporting",
    description: "Regulatory reporting, IAM, audit trails and risk governance validation.",
  },
  {
    title: "Accessibility & Analytics",
    description: "WCAG-driven accessibility validation and analytics data capture quality assurance.",
  },
];

export const certifications = [
  {
    name: "Certified Scrum Master (CSM)",
    issuer: "Scrum Alliance",
    note: "Agile delivery leadership across distributed teams.",
  },
  {
    name: "SAFe Product Owner / Product Manager",
    issuer: "Scaled Agile, Inc.",
    note: "Scaled Agile execution at the program and portfolio tier.",
  },
];

export const education = [
  {
    degree: "Bachelor of Engineering — Aeronautical Engineering",
    institution: "Anna University",
  },
];

export const clients = [
  {
    name: "Bank of America",
    short: "BofA",
    period: "2013 — 2016",
    domain: "Capital Markets · IAM",
    blurb:
      "Functional and integration testing across IAM and regulatory programs during early career foundations.",
    logo: "https://logo.clearbit.com/bankofamerica.com",
    accent: "#E31837",
  },
  {
    name: "BNP Paribas",
    short: "BNP",
    period: "2016 — 2019",
    domain: "Custody · Capital Markets",
    blurb:
      "Hybrid automation framework design, ROI analysis and multi-release validation for custodian and capital markets platforms.",
    logo: "https://logo.clearbit.com/bnpparibas.com",
    accent: "#00915A",
  },
  {
    name: "Bank of Montreal",
    short: "BMO",
    period: "2019 — 2026",
    domain: "Wealth · Portfolio Management",
    blurb:
      "Automation, functional QA and business analysis leadership on the BMO — FNZ wealth management platform migration.",
    logo: "https://logo.clearbit.com/bmo.com",
    accent: "#0079C1",
  },
];

export const sectionImages = {
  domain: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80",
  workspace: "https://images.unsplash.com/photo-1604781109199-ced99b89b0f6?auto=format&fit=crop&w=1400&q=80",
  finance: "https://images.unsplash.com/photo-1508385082359-f38ae991e8f2?auto=format&fit=crop&w=1600&q=70",
};
